import React, { useState, useEffect, useMemo } from 'react';

import { AchievementContext } from './achievements';
import { SAVE_CONFIG } from './constants';
import { getDemoSave, isDemoSlot } from './DemoSave';
import { saveLogger } from './Logger';
import {
  isSaveData,
  parseStoredSlotData,
  serializeSlotData,
  MANUAL_SAVE_SLOT_COUNT,
} from './utils/saveSlots';

// Save slot data interface
interface SaveSlotData {
  name: string;
  timestamp: number;
  playerPos?: { x: number; y: number };
  currentBiome?: string;
  isEmpty?: boolean;
  isAuto?: boolean;
  isDemo?: boolean;
}

// SaveMenu props interface
interface SaveMenuProps {
  saveMenuOpen: boolean;
  onClose: () => void;
  onSave: (saveKey: string, displayName: string) => boolean;
  onLoad: (saveKey: string) => void;
  onNewGame: () => boolean;
  triggerAchievementCheck: (context: AchievementContext) => void;
}

// Save Menu Component
export const SaveMenu: React.FC<SaveMenuProps> = ({
  saveMenuOpen,
  onClose,
  onSave,
  onLoad,
  onNewGame,
  triggerAchievementCheck,
}) => {
  const [saveSlots, setSaveSlots] = useState<SaveSlotData[]>([]);
  const [slotNames, setSlotNames] = useState<string[]>([]);
  const [clipboardSupported, setClipboardSupported] = useState<boolean>(false);
  const [autoSaveSlot, setAutoSaveSlot] = useState<SaveSlotData | null>(null);
  const [pendingLoadSlotIndex, setPendingLoadSlotIndex] = useState<
    number | null
  >(null);

  // Load existing save slots on component mount
  useEffect(() => {
    if (saveMenuOpen) {
      loadSaveSlots();
      setClipboardSupported(Boolean(navigator?.clipboard));
    }
  }, [saveMenuOpen]);

  const loadSaveSlots = (): void => {
    const slots: SaveSlotData[] = [];
    const names: string[] = [];
    let autoSlot: SaveSlotData = {
      name: 'Auto Save',
      timestamp: 0,
      isEmpty: true,
      isAuto: true,
    };

    try {
      const baseSave = localStorage.getItem(SAVE_CONFIG.SAVE_KEY);
      if (baseSave) {
        const parsedBase = JSON.parse(baseSave);
        if (isSaveData(parsedBase)) {
          autoSlot = {
            name: 'Auto Save',
            timestamp: parsedBase.timestamp ?? 0,
            playerPos: parsedBase.playerPos,
            currentBiome: parsedBase.currentBiome,
            isEmpty: false,
            isAuto: true,
          };
        } else {
          saveLogger.error('Invalid auto save data encountered');
        }
      }
    } catch (error) {
      saveLogger.error('Error loading auto save data', { error });
    }
    for (let i = 0; i < MANUAL_SAVE_SLOT_COUNT; i++) {
      // Check if this is the demo slot
      if (isDemoSlot(i)) {
        const demoSave = getDemoSave();
        if (demoSave) {
          try {
            const slotData = parseStoredSlotData(demoSave);
            const { saveData } = slotData;

            slots.push({
              name: 'Demo Save',
              timestamp: slotData.timestamp,
              playerPos: saveData.playerPos,
              currentBiome: saveData.currentBiome,
              isDemo: true,
            });
            names.push('Demo Save');
          } catch (error) {
            saveLogger.error('Error parsing demo save data', { error });
            slots.push({
              name: 'Demo Slot (Empty)',
              timestamp: 0,
              isEmpty: true,
              isDemo: true,
            });
            names.push('Demo Slot');
          }
        } else {
          slots.push({
            name: 'Demo Slot (Empty)',
            timestamp: 0,
            isEmpty: true,
            isDemo: true,
          });
          names.push('Demo Slot');
        }
        continue;
      }

      const saveKey = `${SAVE_CONFIG.SAVE_KEY}_slot_${i}`;
      try {
        const savedData = localStorage.getItem(saveKey);
        if (savedData) {
          try {
            const slotData = parseStoredSlotData(savedData);
            const { saveData } = slotData;

            slots.push({
              name: slotData.name || `Save ${i + 1}`,
              timestamp: slotData.timestamp,
              playerPos: saveData.playerPos,
              currentBiome: saveData.currentBiome,
            });
            names.push(slotData.name || `Save ${i + 1}`);
          } catch (error) {
            saveLogger.error('Error parsing save slot data', {
              slotIndex: i,
              error,
            });
            slots.push({
              name: `Corrupted Slot ${i + 1}`,
              timestamp: 0,
              isEmpty: true,
            });
            names.push('');
          }
        } else {
          slots.push({
            name: `Empty Slot ${i + 1}`,
            timestamp: 0,
            isEmpty: true,
          });
          names.push('');
        }
      } catch (error) {
        saveLogger.error('Error loading save slot', {
          slotIndex: i,
          error,
        });
        slots.push({
          name: `Corrupted Slot ${i + 1}`,
          timestamp: 0,
          isEmpty: true,
        });
        names.push('');
      }
    }
    setSaveSlots(slots);
    setSlotNames(names);
    setAutoSaveSlot(autoSlot);
  };

  const handleSaveToSlot = (slotIndex: number): void => {
    const saveKey = `${SAVE_CONFIG.SAVE_KEY}_slot_${slotIndex}`;
    const slotName = slotNames[slotIndex]?.trim() || `Save ${slotIndex + 1}`;

    const success = onSave(saveKey, slotName);
    if (success) {
      loadSaveSlots();
    }
  };

  const loadSlot = (slotIndex: number): void => {
    const slot = saveSlots[slotIndex];

    if (!slot || slot.isEmpty) {
      return;
    }

    if (slot.isDemo) {
      const demoSave = getDemoSave();
      if (demoSave) {
        try {
          parseStoredSlotData(demoSave);
          const tempKey = `${SAVE_CONFIG.SAVE_KEY}_temp_demo_load`;
          localStorage.setItem(tempKey, demoSave);
          onLoad(tempKey);
          setTimeout(() => localStorage.removeItem(tempKey), 100);
        } catch (error) {
          saveLogger.error('Error loading demo save', { error });
          window.alert('Failed to load demo save.');
        }
      }

      return;
    }

    const saveKey = `${SAVE_CONFIG.SAVE_KEY}_slot_${slotIndex}`;
    onLoad(saveKey);
  };

  const handleLoad = (slotIndex: number): void => {
    const slot = saveSlots[slotIndex];

    if (slot && !slot.isEmpty) {
      setPendingLoadSlotIndex(slotIndex);
    }
  };

  const handleDelete = (slotIndex: number): void => {
    const saveKey = `${SAVE_CONFIG.SAVE_KEY}_slot_${slotIndex}`;
    const slot = saveSlots[slotIndex];

    if (slot && !slot.isEmpty) {
      if (
        window.confirm(`Delete save "${slot.name}"? This cannot be undone.`)
      ) {
        localStorage.removeItem(saveKey);
        loadSaveSlots(); // Refresh the display
      }
    }
  };

  const handleExportSlot = async (slotIndex: number): Promise<void> => {
    if (!clipboardSupported || !navigator.clipboard?.writeText) {
      window.alert('Clipboard API is not available in this browser.');
      return;
    }

    const slot = saveSlots[slotIndex];
    if (!slot || slot.isEmpty) {
      window.alert('There is no save data in the selected slot to export.');
      return;
    }

    const saveKey = `${SAVE_CONFIG.SAVE_KEY}_slot_${slotIndex}`;
    try {
      const savedData = localStorage.getItem(saveKey);
      if (!savedData) {
        window.alert('Unable to find save data for the selected slot.');
        return;
      }

      await navigator.clipboard.writeText(savedData);
      window.alert(
        'Save data copied to clipboard. You can now share or store it safely.'
      );
      triggerAchievementCheck({ saveExported: true });
    } catch (error) {
      saveLogger.error('Failed to export save data', {
        slotIndex,
        error,
      });
      window.alert('Failed to copy save data to the clipboard.');
    }
  };

  const handleImportToSlot = async (slotIndex: number): Promise<void> => {
    if (!clipboardSupported || !navigator.clipboard?.readText) {
      window.alert('Clipboard API is not available in this browser.');
      return;
    }

    const slot = saveSlots[slotIndex];
    if (slot && !slot.isEmpty) {
      const confirmOverwrite = window.confirm(
        `Slot ${slotIndex + 1} already has a save. Overwrite it with the clipboard data?`
      );
      if (!confirmOverwrite) {
        return;
      }
    }

    try {
      const clipboardText = await navigator.clipboard.readText();
      if (!clipboardText.trim()) {
        window.alert('Clipboard is empty. Copy a save string first.');
        return;
      }

      let parsedSlot;
      try {
        parsedSlot = parseStoredSlotData(clipboardText.trim());
      } catch (parseError) {
        saveLogger.error('Clipboard contents are not valid save data', {
          parseError,
        });
        window.alert('Clipboard does not contain valid save data.');
        return;
      }

      const saveKey = `${SAVE_CONFIG.SAVE_KEY}_slot_${slotIndex}`;
      const slotName =
        parsedSlot.name && parsedSlot.name.trim().length > 0
          ? parsedSlot.name
          : `Imported Save ${slotIndex + 1}`;
      const serialized = serializeSlotData(slotName, parsedSlot.saveData);

      localStorage.setItem(saveKey, serialized);
      loadSaveSlots();
      window.alert(`Imported save data into Slot ${slotIndex + 1}.`);
    } catch (error) {
      saveLogger.error('Failed to import save data from clipboard', {
        slotIndex,
        error,
      });
      window.alert('Failed to import save data from the clipboard.');
    }
  };

  const handleNameChange = (slotIndex: number, value: string): void => {
    setSlotNames(prev => {
      const next = [...prev];
      next[slotIndex] = value;
      return next;
    });
  };

  const pendingLoadSlot = useMemo(() => {
    if (pendingLoadSlotIndex === null) {
      return null;
    }

    return saveSlots[pendingLoadSlotIndex] ?? null;
  }, [pendingLoadSlotIndex, saveSlots]);

  const closeLoadConfirmation = (): void => {
    setPendingLoadSlotIndex(null);
  };

  const confirmLoad = (): void => {
    if (pendingLoadSlotIndex === null) {
      return;
    }

    const slotIndex = pendingLoadSlotIndex;
    closeLoadConfirmation();
    loadSlot(slotIndex);
  };

  const formatDate = (timestamp: number): string => {
    if (timestamp === 0) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  if (!saveMenuOpen) return null;

  return (
    <div className='save-menu-overlay'>
      <div className='save-menu'>
        <div className='save-menu-header'>
          <h2>Save & Load Game</h2>
          <button
            type='button'
            className='close-button'
            onClick={onClose}
            title='Close menu'
          >
            ✕
          </button>
        </div>

        <div className='save-menu-content'>
          {autoSaveSlot && (
            <div className='auto-save-section'>
              <h3>Automatic Save</h3>
              <div
                className={`save-slot auto-save-slot ${
                  autoSaveSlot.isEmpty ? 'empty' : 'filled'
                }`}
              >
                <div className='slot-info'>
                  <div className='slot-name'>{autoSaveSlot.name}</div>
                  {autoSaveSlot.isEmpty ? (
                    <div className='empty-slot-text'>
                      No auto save has been created yet. The game will create
                      one automatically as you play.
                    </div>
                  ) : (
                    <div className='slot-details'>
                      <div>Saved: {formatDate(autoSaveSlot.timestamp)}</div>
                      {autoSaveSlot.playerPos && (
                        <div>
                          Position: ({autoSaveSlot.playerPos.x},
                          {autoSaveSlot.playerPos.y})
                        </div>
                      )}
                      {autoSaveSlot.currentBiome && (
                        <div>Biome: {autoSaveSlot.currentBiome}</div>
                      )}
                    </div>
                  )}
                  <div className='auto-save-note'>
                    Auto saves run periodically and cannot be managed manually.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Load Section */}
          <div className='load-section'>
            <h3>Load Saved Game</h3>
            <div className='save-slots'>
              {saveSlots.map((slot, index) => (
                <div
                  key={index}
                  className={`save-slot ${slot.isEmpty ? 'empty' : 'filled'} ${slot.isDemo ? 'demo-slot' : ''}`}
                >
                  <div className='slot-info'>
                    <div className='save-name-input'>
                      <input
                        type='text'
                        placeholder={`Save ${index + 1}`}
                        value={slotNames[index] ?? ''}
                        onChange={event =>
                          handleNameChange(index, event.target.value)
                        }
                        maxLength={30}
                        readOnly={slot.isDemo}
                        disabled={slot.isDemo}
                      />
                    </div>
                    {!slot.isEmpty && (
                      <div className='slot-details'>
                        <div>Saved: {formatDate(slot.timestamp)}</div>
                        {slot.playerPos && (
                          <div>
                            Position: ({slot.playerPos.x}, {slot.playerPos.y})
                          </div>
                        )}
                        {slot.currentBiome && (
                          <div>Biome: {slot.currentBiome}</div>
                        )}
                      </div>
                    )}
                    {slot.isDemo && (
                      <div className='demo-slot-notice'>
                        This is a read-only demo slot. Saving and importing are
                        disabled.
                      </div>
                    )}
                  </div>
                  <div className='slot-actions'>
                    {!slot.isDemo && (
                      <button
                        type='button'
                        onClick={() => handleSaveToSlot(index)}
                        className='save-button'
                      >
                        Save
                      </button>
                    )}
                    {!slot.isDemo && (
                      <button
                        type='button'
                        onClick={() => void handleImportToSlot(index)}
                        className='clipboard-button secondary'
                        disabled={!clipboardSupported}
                        title='Import save data from your clipboard into this slot'
                      >
                        Import
                      </button>
                    )}
                    {!slot.isEmpty && (
                      <button
                        type='button'
                        onClick={event => {
                          event.preventDefault();
                          handleLoad(index);
                        }}
                        className='load-button'
                      >
                        Load
                      </button>
                    )}
                    {!slot.isEmpty && !slot.isDemo && (
                      <button
                        type='button'
                        onClick={() => void handleExportSlot(index)}
                        className='clipboard-button'
                        title='Export this save slot to your clipboard'
                        disabled={!clipboardSupported}
                      >
                        Export
                      </button>
                    )}
                    {!slot.isEmpty && !slot.isDemo && (
                      <button
                        type='button'
                        onClick={event => {
                          event.preventDefault();
                          handleDelete(index);
                        }}
                        className='delete-button'
                        title='Delete this save'
                      >
                        🗑
                      </button>
                    )}
                    {slot.isEmpty && !slot.isDemo && (
                      <button
                        type='button'
                        onClick={onNewGame}
                        className='new-game-button'
                      >
                        Start New Game
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Game Management */}
          <div className='game-management'>
            <button
              type='button'
              onClick={onNewGame}
              className='new-game-button danger'
            >
              New Game
            </button>
          </div>
        </div>
        {pendingLoadSlot && (
          <div
            className='save-confirmation-overlay'
            role='presentation'
            onClick={closeLoadConfirmation}
          >
            <div
              className='save-confirmation-modal'
              role='dialog'
              aria-modal='true'
              aria-labelledby='save-load-confirmation-title'
              aria-describedby='save-load-confirmation-message'
              onClick={event => event.stopPropagation()}
            >
              <h3 id='save-load-confirmation-title'>Load Save?</h3>
              <p id='save-load-confirmation-message'>
                Load "{pendingLoadSlot.name}"? Any unsaved progress will be
                lost.
              </p>
              <div className='save-confirmation-actions'>
                <button
                  type='button'
                  className='clipboard-button secondary'
                  onClick={closeLoadConfirmation}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='load-button'
                  onClick={confirmLoad}
                >
                  Load Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
